import type { TyreLayout, TyreLayoutSubjectKind } from '@chukua/common';
import { Form } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { ARRANGEMENT_PRESETS, arrangementEdit, arrangementFromLayout, type ArrangedAxle } from '@chukua/vehicle-model';
import { detailsFormValue, isDetailsChanged, type VehicleClassDetailsValue } from '../policies/vehicle-class-form.policy';
import type { VehicleClass } from './use-vehicle-class';

/** A new truck class starts as a 6x4, a new trailer type as a tri-axle: the commonest of each. */
const NEW_ARRANGEMENT: Record<TyreLayoutSubjectKind, string> = { truck_class: '6x4', trailer_type: 'tri' };

function startingArrangement(kind: TyreLayoutSubjectKind, subject: VehicleClass | null, layout: TyreLayout | null): ArrangedAxle[] {
  if (layout && layout.axles.length > 0) return arrangementFromLayout(layout);
  if (subject) return [];
  return ARRANGEMENT_PRESETS[kind].find((preset) => preset.id === NEW_ARRANGEMENT[kind])?.axles ?? [];
}

/**
 * The page's form: the row's details in an Ant form, and the axle
 * arrangement beside it. The tyre positions are never typed: they come from
 * the arrangement, so the layout is saved only when the arrangement changed,
 * and a layout someone typed by hand before keeps its codes until then.
 */
export function useVehicleClassEditor(kind: TyreLayoutSubjectKind, subject: VehicleClass | null, layout: TyreLayout | null) {
  const [form] = Form.useForm<VehicleClassDetailsValue>();
  const initial = useMemo(() => startingArrangement(kind, subject, layout), [kind, subject, layout]);
  /** Keyed to the saved arrangement, so a save or a reload starts the editor over from it (React's "adjust state when a prop changes"). */
  const [edited, setEdited] = useState({ from: initial, axles: initial });
  if (edited.from !== initial) setEdited({ from: initial, axles: initial });
  const axles = edited.from === initial ? edited.axles : initial;
  const setAxles = (next: ArrangedAxle[]) => setEdited({ from: initial, axles: next });
  const watched = Form.useWatch([], form) as VehicleClassDetailsValue | undefined;

  useEffect(() => {
    form.setFieldsValue(detailsFormValue(kind, subject));
  }, [form, kind, subject]);

  const isNew = subject === null;
  const edit = arrangementEdit(axles, { initial, layout, isNew });

  return {
    form,
    axles,
    setAxles,
    bodyStyle: watched?.bodyStyle ?? subject?.bodyStyle ?? null,
    shownAxles: edit.shownAxles,
    layoutInput: edit.layoutInput,
    retired: edit.retired,
    isChanged: isNew || edit.isChanged || isDetailsChanged(kind, subject, watched),
    discard() {
      form.setFieldsValue(detailsFormValue(kind, subject));
      setAxles(initial);
    },
  };
}
